// Hooks TanStack Query del wizard (EJE FRONTEND, ARQUITECTURA_FRONT_PROPUESTA §7). Componen la
// facade `wizardApi` (mock/real por env). Query keys namespaced ['wizard', ...] para invalidación
// aislada. La sesión es la FUENTE DE VERDAD (se re-lee del silo en cada paso, nunca de estado local
// — puede reabrirse en otra tablet, convención transversal de `pantallas-wizard.html`).
import { useCallback, useRef, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { wizardApi } from '@/api/services/retail-wizard';
import type {
    CancelOtherStoreSaleRequest,
    SessionReferralInput,
    SetOccupationInput,
    UpdateSessionReferralInput,
} from '@/api/services/retail-wizard/contract';
import { QUOTE_DISCARDED_MESSAGE, quoteDiscarded } from '../quoteInvalidation';
import { laEsperaSeAgoto } from '../kycEspera';
import {
    ATTEMPT_POLL_MS,
    kycPollInterval,
    pollCapStart,
    polledFlow,
    type AwaitedAttempt,
    type KycPollScope,
} from '../metamapAttempt';
import { useElapsedSince } from './useElapsedSince';
import { accountPollInterval, stillWaitingForAccount, type BlockedNotice } from '../accountWait';
import { deviceEnrollmentPollInterval, isTransientRequestError } from '../deviceEnrollment';
import { invalidateStockQueries } from './stockQueries';
import type {
    KycState,
    AccountStatusResult,
    AddProductRequest,
    Beneficiary,
    CompleteSaleLine,
    PinPiecesLine,
    ProfileAnswers,
    CreateProspectRequest,
    CreateSessionRequest,
    EmailKind,
    MetamapFlow,
    QuoteTerms,
    RegisterPaymentRequest,
    ReleaseProductTarget,
    WizardSession,
} from '../types';

export const WIZARD_KEYS = {
    session: (id: string) => ['wizard', 'session', id] as const,
    kyc: (id: string) => ['wizard', 'kyc', id] as const,
    account: (id: string) => ['wizard', 'account', id] as const,
    // Sin `sort`/`q`: el catálogo de la tienda es UNO solo y se filtra en el cliente (ver
    // `catalogFilters.ts`). Cuando la búsqueda viajaba en la key, cada término era un cache miss.
    catalog: (id: string) => ['wizard', 'catalog', id] as const,
    financiers: (id: string) => ['wizard', 'financiers', id] as const,
    score: (id: string) => ['wizard', 'score', id] as const,
    contract: (id: string) => ['wizard', 'contract', id] as const,
    profile: (id: string) => ['wizard', id, 'profile'] as const,
    prospects: (sellerId: string) => ['wizard', 'prospects', sellerId] as const,
    prospectByPhone: (phone: string) => ['wizard', 'prospect-by-phone', phone] as const,
    activeSession: (phone: string) => ['wizard', 'active-session', phone] as const,
    /** Prefijo de todas las ventas activas: cancelar o guardar no siempre conoce el teléfono. */
    activeSessions: () => ['wizard', 'active-session'] as const,
    appInstall: (id: string) => ['wizard', 'app-install', id] as const,
    appOnboarding: (id: string) => ['wizard', 'app-onboarding', id] as const,
    deviceEnrollment: (id: string) => ['wizard', 'device-enrollment', id] as const,
    referrals: (id: string) => ['wizard', 'referrals', id] as const,
    identifiers: (id: string, lineId: string, sku: string, prefix: string) =>
        ['wizard', 'identifiers', id, lineId, sku, prefix] as const,
};

/** Caracteres mínimos que el silo exige para sugerir IMEI o series. */
export const MIN_IDENTIFIER_PREFIX = 4;

/** Sesión completa — se re-lee en cada montaje de paso (resume desde el silo). */
export function useWizardSession(sessionId: string) {
    const qc = useQueryClient();
    const query = useQuery({
        queryKey: WIZARD_KEYS.session(sessionId),
        queryFn: () => wizardApi.getSession(sessionId),
        enabled: !!sessionId,
    });
    const refresh = () => qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
    // Object.assign (no object-spread) para no disparar @tanstack/query/no-rest-destructuring.
    return Object.assign(query, { refresh });
}

/** Mutación con argumento que invalida la sesión al terminar. Preserva el tipo de retorno. */
function useSessionMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) }),
    });
}

/**
 * Mutación que escribe en el EXPEDIENTE de Fiado (ocupación, perfil de PLD, beneficiarios).
 * Invalida la sesión Y el perfil: el dato vive en Fiado, no en el espejo de la sesión, y con el
 * `staleTime` de 60 s la pantalla seguiría mostrando lo de antes de guardar.
 */
function useProfileMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
        },
    });
}

/** Mutación SIN argumento (mutateAsync()) que invalida la sesión al terminar. */
function useSessionAction<TData>(fn: () => Promise<TData>, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) }),
    });
}

/**
 * Mutación que TOCA EL CRÉDITO (re-cotizar / cobrar enganche / firmar / registrar equipo):
 * invalida sesión Y contract-preview. Las pantallas 07-09 rehidratan montos y estado del crédito
 * de `contract/preview` (staleTime 60 s) — sin esta invalidación, re-cotizar $6,250 y volver al
 * enganche mostraba el monto viejo de la caché (bug real cazado en pruebas).
 */
function useCreditMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.contract(sessionId) });
        },
    });
}

// ── 01 · OTP + términos ───────────────────────────────────────────────────
export function useCreateSession() {
    return useMutation({ mutationFn: (req: CreateSessionRequest) => wizardApi.createSession(req) });
}
/** «Cancelar venta» del vendedor: cancela la sesión y relee la venta y las existencias que soltó. */
export function useCancelSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: { sessionId: string; reason: string }) =>
            wizardApi.cancelSession(args.sessionId, args.reason),
        onSettled: (_data, _error, args) =>
            Promise.all([
                qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(args.sessionId) }),
                qc.invalidateQueries({ queryKey: WIZARD_KEYS.activeSessions() }),
                invalidateStockQueries(qc),
            ]),
    });
}
/** El encargado cancela la venta de otra sucursal que retiene el teléfono. */
export function useCancelPhoneHolder() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (req: CancelOtherStoreSaleRequest) => wizardApi.cancelPhoneHolder(req),
        onSettled: () =>
            Promise.all([
                qc.invalidateQueries({ queryKey: WIZARD_KEYS.activeSessions() }),
                invalidateStockQueries(qc),
            ]),
    });
}
/**
 * Retoma una venta a medias: deja el OTP pendiente y devuelve el paso al que hay que ir. Sin
 * `sessionId` fijo porque el conflicto de teléfono lo descubre la pantalla de captura.
 */
export function useResumeSession() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (sessionId: string) => wizardApi.resumeSession(sessionId),
        onSuccess: (_data, sessionId) =>
            qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) }),
    });
}
export function useVerifyOtp(sessionId: string) {
    return useSessionMutation((code: string) => wizardApi.verifyOtp(sessionId, code), sessionId);
}
export function useResendOtp(sessionId: string) {
    return useMutation({ mutationFn: () => wizardApi.resendOtp(sessionId) });
}
export function useAcceptConsents(sessionId: string) {
    return useSessionAction(() => wizardApi.acceptConsents(sessionId), sessionId);
}
/** Manda el código al correo; la sesión se relee para pintar el correo pendiente. */
export function useSendEmailOtp(sessionId: string) {
    return useSessionMutation(
        (args: { email: string; kind: EmailKind }) =>
            wizardApi.sendEmailOtp(sessionId, args.email, args.kind),
        sessionId,
    );
}
/** Verifica el código del correo; la sesión se relee para pintar el correo verificado. */
export function useVerifyEmailOtp(sessionId: string) {
    return useSessionMutation(
        (code: string) => wizardApi.verifyEmailOtp(sessionId, code),
        sessionId,
    );
}

// ── 02 · KYC + ocupación ──────────────────────────────────────────────────
/**
 * ¿La última lectura de `kyc/status` todavía no explica el FAILED? Un error corta el intervalo
 * (queda el retry normal) y una lectura que ya ve el FAILED sin code no va a traer más.
 */
function stillMissingFailureCode(state: { status: string; data?: KycState }): boolean {
    if (state.status === 'error') return false;
    return !state.data?.failure && state.data?.status !== 'FAILED';
}

export interface KycPollOptions extends KycPollScope {
    /** La sesión ya cerró en FAILED y la pantalla depende del `kycFailure.code`. */
    awaitingFailureCode: boolean;
}

/** Estado del KYC por intento (D20). Se lee siempre al montar; el intervalo depende del intento a la vista. */
export function useKycAttemptPoll(sessionId: string, options: KycPollOptions) {
    const qc = useQueryClient();
    const [restartToken, setRestartToken] = useState(0);
    const [awaited, setAwaited] = useState<AwaitedAttempt | null>(null);
    const [mountedAt] = useState(() => new Date().toISOString());
    // El caché da el intento antes de que el poll lea; sin intento el tope cuenta desde el montaje.
    const cached = qc.getQueryData<KycState>(WIZARD_KEYS.kyc(sessionId));
    const msSinceAttempt = useElapsedSince(
        pollCapStart(cached, polledFlow(cached, options), awaited, mountedAt),
        restartToken,
    );
    const attemptTimedOut = laEsperaSeAgoto(msSinceAttempt);

    const query = useQuery({
        queryKey: WIZARD_KEYS.kyc(sessionId),
        queryFn: () => wizardApi.kycStatus(sessionId),
        enabled: !!sessionId,
        // El gate del comprobante sale de aquí: recargar o volver a la pestaña siempre relee.
        refetchOnMount: 'always',
        refetchOnWindowFocus: 'always',
        refetchInterval: (q) => {
            if (options.awaitingFailureCode && stillMissingFailureCode(q.state)) {
                return ATTEMPT_POLL_MS;
            }
            // Tras un prepare se pregunta hasta que el poll traiga ese intento, aunque el viejo ya cerró.
            const seen = awaited ? q.state.data?.progress?.[awaited.flow]?.attemptId : undefined;
            if (awaited && !attemptTimedOut && seen !== awaited.attemptId) return ATTEMPT_POLL_MS;
            return kycPollInterval(
                q.state.data,
                polledFlow(q.state.data, options),
                attemptTimedOut,
                q.state.dataUpdateCount,
            );
        },
    });

    const restartAttemptClock = useCallback(() => {
        setRestartToken((token) => token + 1);
    }, []);
    const awaitAttempt = useCallback((flow: MetamapFlow, attemptId: string) => {
        setAwaited({ flow, attemptId, awaitedAt: new Date().toISOString() });
    }, []);
    // Object.assign (no object-spread) para no disparar @tanstack/query/no-rest-destructuring.
    return Object.assign(query, { attemptTimedOut, restartAttemptClock, awaitAttempt });
}
/** Nombre que verificó el KYC de la venta; la sesión no lo trae. Solo se pide con el drawer abierto. */
export function useVerifiedName(sessionId: string, enabled: boolean): string | undefined {
    const { data } = useQuery({
        queryKey: WIZARD_KEYS.kyc(sessionId),
        queryFn: () => wizardApi.kycStatus(sessionId),
        enabled: enabled && Boolean(sessionId),
    });
    return data?.verified?.fullName;
}
export function useKycSendLink(sessionId: string) {
    return useMutation({ mutationFn: () => wizardApi.kycSendLink(sessionId) });
}
export function useKycManual(sessionId: string) {
    return useSessionMutation(
        (data: { curp: string; fullName: string; birthDate: string }) =>
            wizardApi.kycManual(sessionId, data),
        sessionId,
    );
}
export function useSetOccupation(sessionId: string) {
    return useProfileMutation(
        (args: SetOccupationInput) => wizardApi.setOccupation(sessionId, args),
        sessionId,
    );
}

/**
 * Estado de la cuenta PagoConFiado — el bloqueo del final del paso 02.
 *
 * `waiting` controla el POLL, no la lectura: las estaciones y el motivo del rechazo son justo lo que
 * hay que seguir viendo DESPUÉS de que la espera terminó.
 *
 * El corte por desenlace lo decide la propia respuesta (`stillWaitingForAccount`), no el llamador.
 * `msWaiting` entra porque un expediente incompleto solo corta el poll pasado su margen.
 * `noticeFor` dice qué letrero se pinta con esa respuesta: el amarillo pollea cada 10 s sin tope.
 */
export function useAccountStatus(
    sessionId: string,
    waiting: boolean,
    msWaiting: number | null = null,
    noticeFor: (status: AccountStatusResult | undefined, error: unknown) => BlockedNotice = () =>
        'NONE',
) {
    return useQuery({
        queryKey: WIZARD_KEYS.account(sessionId),
        queryFn: () => wizardApi.accountStatus(sessionId),
        enabled: !!sessionId,
        refetchInterval: (query) =>
            accountPollInterval(
                waiting && stillWaitingForAccount(query.state.data, query.state.error, msWaiting),
                query.state.dataUpdateCount,
                noticeFor(query.state.data, query.state.error),
            ),
    });
}

// ── 03 · Producto ─────────────────────────────────────────────────────────
/**
 * Catálogo de la tienda — UNA sola lectura, sin parámetros de búsqueda.
 *
 * 🔴 **`q` y `sort` ya NO viajan al silo.** Nunca sirvieron: `GET /wizard/catalog` solo lee
 * `storeId` (ver `PublicController.getCatalog` de retail-wizard-business) y descarta cualquier otro
 * query param. Mandarlos costaba tres saltos de lambda (authorizer → retail-org → retail-catalog)
 * para devolver exactamente la misma lista, y como iban en el query key, cada búsqueda era un cache
 * miss nuevo. El filtrado real vive en `catalogFilters.ts`, sobre la lista ya en memoria.
 *
 * `staleTime` de 60 s = el "cache 60s en el frontend" que pide la ficha 03; el refresco manual del
 * vendedor sale por el `refetch` de esta misma query. `cached` sirve la caché sin re-pedirla al montar.
 */
export function useCatalog(sessionId: string, { cached = false }: { cached?: boolean } = {}) {
    return useQuery({
        queryKey: WIZARD_KEYS.catalog(sessionId),
        queryFn: () => wizardApi.getCatalog(sessionId),
        enabled: !!sessionId,
        staleTime: cached ? Infinity : 60_000,
        // Al refrescar a mano, la grilla anterior se mantiene en pantalla (sin parpadeo).
        placeholderData: keepPreviousData,
    });
}

/**
 * Mutación de una línea: el silo responde la sesión completa, así que se escribe directo en caché
 * (sin releerla) y el catálogo queda viejo porque su disponible cambió.
 */
function useLineMutation<TArgs>(fn: (args: TArgs) => Promise<WizardSession>, sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: (session) => {
            const before = qc.getQueryData<WizardSession>(WIZARD_KEYS.session(sessionId));
            if (quoteDiscarded(before, session)) {
                toast.info(QUOTE_DISCARDED_MESSAGE);
            }
            qc.setQueryData(WIZARD_KEYS.session(sessionId), session);
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.catalog(sessionId) });
        },
    });
}

/** Agrega una línea (`{ sku, qty }`) y la aparta 30 min; la llave estable evita apartar dos veces. */
export function useSelectProduct(sessionId: string) {
    return useLineMutation(
        ({ idempotencyKey, ...req }: AddProductRequest & { idempotencyKey?: string }) =>
            wizardApi.selectProduct(sessionId, req, idempotencyKey),
        sessionId,
    );
}

/** Renueva el apartado de una línea (vencido o no) sin apartar otra unidad. */
export function useRenewLineReservation(sessionId: string) {
    return useLineMutation(
        (lineId: string) => wizardApi.renewLineReservation(sessionId, lineId),
        sessionId,
    );
}

/** Quita una línea por su `lineId`; `{ imei }` solo en sesiones anteriores a las líneas. */
export function useReleaseProduct(sessionId: string) {
    return useLineMutation(
        (target: ReleaseProductTarget) => wizardApi.releaseProduct(sessionId, target),
        sessionId,
    );
}

/** IMEI o series disponibles de un SKU de la línea que empiezan con `prefix`. */
export function useSuggestIdentifiers(
    sessionId: string,
    lineId: string,
    sku: string,
    prefix: string,
) {
    const trimmed = prefix.trim();
    return useQuery({
        queryKey: WIZARD_KEYS.identifiers(sessionId, lineId, sku, trimmed),
        queryFn: () => wizardApi.suggestIdentifiers(sessionId, lineId, sku, trimmed),
        enabled: !!sessionId && !!lineId && !!sku && trimmed.length >= MIN_IDENTIFIER_PREFIX,
        staleTime: 10_000,
    });
}

// ── 04 · Forma de pago ─────────────────────────────────────────────────────
/** Financieras del paso 04 (SureKeep + externas elegibles del producto reservado). */
export function useFinanciers(sessionId: string) {
    return useQuery({
        queryKey: WIZARD_KEYS.financiers(sessionId),
        queryFn: () => wizardApi.listFinanciers(sessionId),
        enabled: !!sessionId,
        staleTime: 60_000,
    });
}

export function useSetPaymentMethod(sessionId: string) {
    return useSessionMutation(
        (method: 'CASH_OR_CARD' | 'SUREKEEP') => wizardApi.setPaymentMethod(sessionId, method),
        sessionId,
    );
}
export function useExternalReferral(sessionId: string) {
    return useSessionMutation(
        (provider: string) => wizardApi.externalReferral(sessionId, provider),
        sessionId,
    );
}

// ── 05 · Score ─────────────────────────────────────────────────────────────
/** Dispara la evaluación SCI — decisión DIRECTA (idempotente). Invalida la sesión (loan.level). */
export function useScoreConsult(sessionId: string) {
    return useSessionAction(() => wizardApi.scoreConsult(sessionId), sessionId);
}

/**
 * Score adelantado: se dispara SOLO desde el paso 03 (ver `ProductStep`), antes de que el cliente
 * elija método de pago. Es idempotente en el silo — llamar de más no re-evalúa — pero además de la
 * sesión invalida el CATÁLOGO, porque `affordableOnCredit` y el badge por nivel dependen del nivel
 * que este consult acaba de resolver.
 */
export function useScoreConsultSilent(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => wizardApi.scoreConsult(sessionId),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.catalog(sessionId) });
        },
    });
}

/** Relee el snapshot ya persistido (resume). Solo con `session.loan.sciEvaluationId` presente. */
export function useScoreResult(sessionId: string, enabled: boolean) {
    return useQuery({
        queryKey: WIZARD_KEYS.score(sessionId),
        queryFn: () => wizardApi.scoreResult(sessionId),
        enabled: !!sessionId && enabled,
        staleTime: Infinity, // el snapshot de la evaluación es inmutable
    });
}

// ── 06 · Cotización ────────────────────────────────────────────────────────
/**
 * Cotiza contra el silo: la 1ª llamada ORIGINA el crédito (idempotente por sesión); las
 * siguientes re-cotizan términos sobre el crédito PENDING. Invalida la sesión (loan.creditId).
 */
export function useQuoteCredit(sessionId: string) {
    return useCreditMutation((terms: QuoteTerms) => wizardApi.quote(sessionId, terms), sessionId);
}

// ── 07 · Cobro ─────────────────────────────────────────────────────────────
export function useFolio(sessionId: string) {
    return useSessionAction(() => wizardApi.getFolio(sessionId), sessionId);
}
export function useRegisterPayment(sessionId: string) {
    return useSessionMutation(
        (req: RegisterPaymentRequest) => wizardApi.registerPayment(sessionId, req),
        sessionId,
    );
}

// ── 07-08 · Enganche + contrato (pista crédito) ────────────────────────────
/** Cobra el ENGANCHE (folio ENG server-side + condición 1). Invalida sesión + preview del crédito. */
export function useDownPayment(sessionId: string) {
    return useCreditMutation(
        (req: RegisterPaymentRequest) => wizardApi.downPayment(sessionId, req),
        sessionId,
    );
}
/** Resumen del crédito + variables del contrato — rehidrata montos del 07 y alimenta el 08. */
export function useContractPreview(sessionId: string, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.contract(sessionId),
        queryFn: () => wizardApi.contractPreview(sessionId),
        enabled: !!sessionId && enabled,
        staleTime: 60_000,
    });
}
/** Enlace al PDF del contrato: se pide en cada apertura porque la URL firmada vence en minutos. */
export function useContractDocument(sessionId: string) {
    return useMutation({ mutationFn: () => wizardApi.contractDocument(sessionId) });
}
/** Firma tiempo 1: congela términos + PDF + OTP al celular. Re-disparar REENVÍA el código. */
export function useSignatureSendOtp(sessionId: string) {
    return useMutation({ mutationFn: () => wizardApi.signatureSendOtp(sessionId) });
}
/** Firma tiempo 2: verifica el OTP (4 díg) y firma. Invalida sesión + preview (checklist cambió). */
export function useSignatureSign(sessionId: string) {
    return useCreditMutation(
        (otpCode: string) => wizardApi.signatureSign(sessionId, otpCode),
        sessionId,
    );
}

// ── 09 · Entrega ───────────────────────────────────────────────────────────
/**
 * Tiempo 1: registra el IMEI y abre el enrolamiento (`PENDING`) devolviendo el APK del candado.
 * Una idempotency-key por intento de (sesión, IMEI); solo un fallo pasajero la conserva para el reintento.
 */
export function useRegisterDevice(sessionId: string) {
    const keys = useRef(new Map<string, string>());
    return useCreditMutation(async (imei: string) => {
        const attempt = `${sessionId}:${imei}`;
        const key = keys.current.get(attempt) ?? crypto.randomUUID();
        keys.current.set(attempt, key);
        try {
            const result = await wizardApi.registerDevice(sessionId, imei, key);
            keys.current.delete(attempt);
            return result;
        } catch (error) {
            // Un rechazo definitivo también cierra el intento: el siguiente clic ya es otro.
            if (!isTransientRequestError(error)) keys.current.delete(attempt);
            throw error;
        }
    }, sessionId);
}

/**
 * Tiempo 2: el estado del enrolamiento mientras el vendedor sigue los pasos en el equipo. Trae
 * también el `lockAppUrl` del QR, así que la pantalla se puede recargar sin perder el instructivo.
 * Los cortes del poll viven en `deviceEnrollment.ts`.
 */
export function useDeviceEnrollment(sessionId: string, enabled = true) {
    // Errores acumulados al último éxito: la diferencia es la racha de fallos seguidos.
    const errorsAtLastSuccess = useRef(0);
    return useQuery({
        queryKey: WIZARD_KEYS.deviceEnrollment(sessionId),
        queryFn: () => wizardApi.getDeviceEnrollment(sessionId),
        enabled: !!sessionId && enabled,
        // Los reintentos los decide el poll (backoff ante fallos pasajeros), no TanStack.
        retry: false,
        refetchInterval: (query) => {
            const { data, error, errorUpdateCount } = query.state;
            if (error == null) errorsAtLastSuccess.current = errorUpdateCount;
            const failures = errorUpdateCount - errorsAtLastSuccess.current;
            return deviceEnrollmentPollInterval(data, error, failures);
        },
    });
}
/** Fija las piezas capturadas con /pieces; la respuesta es la sesión y se escribe en caché. */
export function usePinPieces(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (lines: PinPiecesLine[]) => wizardApi.pinPieces(sessionId, lines),
        onSuccess: (session) => {
            qc.setQueryData(WIZARD_KEYS.session(sessionId), session);
        },
    });
}
/** Cierra la venta; con líneas manda los identificadores de cada pieza serializada. */
export function useCompleteSale(sessionId: string) {
    return useSessionMutation(
        (lines?: CompleteSaleLine[]) =>
            lines ? wizardApi.completeSale(sessionId, lines) : wizardApi.completeSale(sessionId),
        sessionId,
    );
}
/** Cierra la apertura de cuenta; la respuesta es la sesión cerrada y se escribe en caché. */
export function useCompleteAccountOpening(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: () => wizardApi.completeAccountOpening(sessionId),
        onSuccess: (session) => {
            qc.setQueryData(WIZARD_KEYS.session(sessionId), session);
        },
    });
}
/**
 * Enlace corto que el cliente escanea para instalar la app (D9), pedido al entrar.
 *
 * Query y no mutación porque el QR de la venta de contado tiene que estar pintado ANTES de que el
 * vendedor haga nada: si esperara a un clic, la pantalla mostraría un cuadro vacío justo cuando el
 * cliente saca su teléfono. El shortlink es estable por sesión → se cachea y no se re-pide al
 * reenfocar la ventana.
 *
 * La pista de crédito lo pide a mano (`useGenerateAppInstallLink`): ahí la liga se lee en voz alta,
 * no se escanea, y el vendedor decide cuándo generarla.
 *
 * `enabled` en false para el paso que se brinca: pedirlo generaría un shortlink que nadie escanea.
 */
export function useAppInstallLink(sessionId: string, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.appInstall(sessionId),
        queryFn: () => wizardApi.getAppInstallLink(sessionId),
        enabled: !!sessionId && enabled,
        staleTime: Infinity,
    });
}

/** La cadencia del paso 09: el cliente está en su app en este mismo momento. */
const APP_ONBOARDING_POLL_MS = 5000;

/**
 * Las 4 condiciones que el cliente cumple desde su app — la única fuente del gate del paso 09.
 *
 * El corte por desenlace lo decide la propia respuesta (`allSettled`), no el llamador.
 *
 * `enabled` en false para el paso que se brinca: ese comprador ya trae su app y su tarjeta.
 */
export function useAppOnboarding(sessionId: string, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.appOnboarding(sessionId),
        queryFn: () => wizardApi.getAppOnboarding(sessionId),
        enabled: !!sessionId && enabled,
        refetchInterval: (query) =>
            query.state.data?.allSettled === true ? false : APP_ONBOARDING_POLL_MS,
    });
}

/** Misma liga, pedida bajo demanda (pista de crédito). */
export function useGenerateAppInstallLink(sessionId: string) {
    return useMutation({ mutationFn: () => wizardApi.getAppInstallLink(sessionId) });
}
/** Sin argumento: el inventario va por cantidad, así que no hay número de tarjeta que mandar. */
export function useMarkCardDelivered(sessionId: string) {
    return useSessionAction(() => wizardApi.markCardDelivered(sessionId), sessionId);
}
// ── 10 · Bienvenida ────────────────────────────────────────────────────────
/**
 * Bono de bienvenida vigente de la cadena: si aplica y su monto (lo que se muestra = lo que se deposita).
 * `enabled` en false para el comprador que ya traía cuenta: a ese el silo le salta el bono.
 */
export function useWelcomeBonus(sessionId: string, enabled = true) {
    return useQuery({
        queryKey: ['wizard', 'welcome-bonus', sessionId] as const,
        queryFn: () => wizardApi.getWelcomeBonus(sessionId),
        enabled: Boolean(sessionId) && enabled,
        staleTime: 5 * 60_000, // promo de la cadena: cambia poco, se relee al re-entrar
    });
}

/**
 * Estado del perfil del paso 10. Se lee de la FUENTE (People + address-lambda) por el wizard, así que
 * NO se deriva de la sesión: fue justo mirar el espejo lo que hizo que la pantalla dijera "—" en el
 * domicilio mientras el dato ya estaba guardado en Fiado.
 */
export function useProfile(sessionId: string, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.profile(sessionId),
        queryFn: () => wizardApi.getProfile(sessionId),
        enabled: Boolean(sessionId) && enabled,
        // El paso 02 vive polleando el KYC y la cuenta. El perfil no cambia con esos ticks: se lee
        // una vez y solo se relee cuando algo lo mueve de verdad (guardar un domicilio lo invalida).
        staleTime: 60_000,
    });
}

/** Buscador de domicilios. `enabled` solo con 3+ caracteres: cada tecla es una llamada a AWS Place. */
export function useAddressSearch(sessionId: string, q: string) {
    return useQuery({
        queryKey: ['wizard', sessionId, 'address-search', q],
        queryFn: () => wizardApi.searchAddress(sessionId, q),
        enabled: Boolean(sessionId) && q.trim().length >= 3,
    });
}

export function useSetProfileQuestions(sessionId: string) {
    return useProfileMutation(
        (answers: ProfileAnswers) => wizardApi.setProfileQuestions(sessionId, answers),
        sessionId,
    );
}

/**
 * Guarda el domicilio. Invalida la sesión Y el perfil: la lista de domicilios sale del address-lambda,
 * no del espejo de la sesión, así que sin esto la pantalla seguía mostrando la lista vieja.
 */
export function useConfirmAddress(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (args: { useIne: boolean; address?: Record<string, unknown> }) =>
            wizardApi.confirmAddress(sessionId, args.useIne, args.address),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.profile(sessionId) });
        },
    });
}
export function useSetBeneficiary(sessionId: string) {
    return useProfileMutation(
        (bs: Beneficiary[]) => wizardApi.setBeneficiary(sessionId, bs),
        sessionId,
    );
}
export function useSignAnnex(sessionId: string) {
    return useSessionAction(() => wizardApi.signAnnex(sessionId), sessionId);
}
export function useWelcomeTopup(sessionId: string) {
    return useSessionAction(() => wizardApi.welcomeTopup(sessionId), sessionId);
}
export function useExitWithoutTopup(sessionId: string) {
    return useSessionAction(() => wizardApi.exitWithoutTopup(sessionId), sessionId);
}

/** Referidos que el cliente ya tiene, para pintarlos arriba de los renglones nuevos. */
export function useSessionReferrals(sessionId: string) {
    return useQuery({
        queryKey: WIZARD_KEYS.referrals(sessionId),
        queryFn: () => wizardApi.listSessionReferrals(sessionId),
        enabled: !!sessionId,
    });
}

/** Guardar u omitir mueven el cursor: invalidan la sesión y la lista de referidos. */
function useReferralsMutation<TArgs, TData>(
    fn: (args: TArgs) => Promise<TData>,
    sessionId: string,
) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: fn,
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.session(sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.referrals(sessionId) });
        },
    });
}

export function useSaveSessionReferrals(sessionId: string) {
    return useReferralsMutation(
        (referrals: SessionReferralInput[]) => wizardApi.saveSessionReferrals(sessionId, referrals),
        sessionId,
    );
}

/** Corrige un referido ya guardado; refresca la lista de referidos de la sesión. */
export function useUpdateSessionReferral(sessionId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({
            referralId,
            patch,
        }: {
            referralId: string;
            patch: UpdateSessionReferralInput;
        }) => wizardApi.updateSessionReferral(sessionId, referralId, patch),
        onSuccess: () => {
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.referrals(sessionId) });
        },
    });
}

export function useSkipReferrals(sessionId: string) {
    return useReferralsMutation<undefined, null>(
        () => wizardApi.skipReferrals(sessionId),
        sessionId,
    );
}

/**
 * Prende la consulta de la venta en curso. Apagarla es la salida si `GET /wizard/sessions` deja de
 * estar: sin la ruta, API Gateway responde 403 sin `code` y el cliente lo lee como sesión vieja, así
 * que cada prospecto que se abre dispara un refresh de token.
 */
export const ACTIVE_SESSION_LOOKUP_ENABLED: boolean = true;

/**
 * Venta en curso de un teléfono, para la pestaña Wizard de Prospectos.
 *
 * Sin teléfono no hay llave con qué buscarla, y en un prospecto ya resuelto (`enabled` en false) la
 * respuesta sería `found: false` por definición: su venta se cerró o se dio por perdida.
 */
export function useActiveWizardSession(phone: string | null, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.activeSession(phone ?? ''),
        queryFn: () => wizardApi.getActiveSessionByPhone(phone ?? ''),
        enabled: ACTIVE_SESSION_LOOKUP_ENABLED && Boolean(phone) && enabled,
        staleTime: 60_000,
    });
}

// ── Prospectos (drawer transversal) ────────────────────────────────────────
/** La agenda del vendedor; `enabled` en falso no la pide (p. ej. sin `retail.wizard.operate`). */
export function useProspects(sellerId: string, enabled = true) {
    return useQuery({
        queryKey: WIZARD_KEYS.prospects(sellerId),
        queryFn: () => wizardApi.listProspects(sellerId),
        enabled: !!sellerId && enabled,
    });
}

/**
 * Apaga la precarga del drawer. Sin la ruta en el API, un 403 se lee como token vencido y cuesta un
 * refresh de sesión por cada apertura — por eso nace apagada y se prende con el silo ya desplegado.
 */
export const PROSPECT_LOOKUP_ENABLED: boolean = true;

/**
 * El prospecto abierto de ese teléfono, para precargar el drawer. Sin reintento y sin error a la
 * pantalla: perder la precarga es molesto, no poder guardar y salir es peor.
 *
 * Sin caché (`gcTime: 0`): al reanudar la venta, la respuesta de antes de guardar precargaría el
 * motivo que el vendedor ya corrigió, y reenviarlo da 422 sin salida.
 */
export function useProspectByPhone(phone: string, enabled: boolean) {
    return useQuery({
        queryKey: WIZARD_KEYS.prospectByPhone(phone),
        queryFn: () => wizardApi.getProspectByPhone(phone),
        enabled: PROSPECT_LOOKUP_ENABLED && Boolean(phone) && enabled,
        retry: false,
        gcTime: 0,
        staleTime: 0,
    });
}

export function useCreateProspect(sellerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (req: CreateProspectRequest) => wizardApi.createProspect(req),
        onSuccess: () =>
            Promise.all([
                qc.invalidateQueries({ queryKey: WIZARD_KEYS.prospects(sellerId) }),
                qc.invalidateQueries({ queryKey: WIZARD_KEYS.activeSessions() }),
            ]),
    });
}
export function useMarkProspectLost(sellerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ prospectId, reason }: { prospectId: string; reason: string }) =>
            wizardApi.markProspectLost(prospectId, reason),
        onSuccess: () => qc.invalidateQueries({ queryKey: WIZARD_KEYS.prospects(sellerId) }),
    });
}
/** Resuelve «Reabrir» en el silo; el error sube con su `code`. Quita la sesión de la caché para leer el reclamo. */
export function useResumeProspect(sellerId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (prospectId: string) => wizardApi.resumeProspect(prospectId),
        onSuccess: (res) => {
            if (res.sessionId) qc.removeQueries({ queryKey: WIZARD_KEYS.session(res.sessionId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.prospects(sellerId) });
            void qc.invalidateQueries({ queryKey: WIZARD_KEYS.activeSessions() });
        },
    });
}
